import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RENTAL_ITEM_ATTACHMENT_TARGET, RENTAL_ITEM_TARGET, USER_TARGET } from "../../config/constants";
import socket, { USER_MESSAGE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError } from "../helpers";
import { IUser, tryConfirmLogin } from "./authReducer";

class SendMessageErrs {
    form: string | null = null;

    parentId: string | null = null;
    senderUserId: string | null = null;
    targetType: string | null = null;
    targetId: string | null = null;
    content: string | null = null;

}

export interface IMessage {
    _id: string;
    sender: IUser;
    receiver: IUser;
    content: string;
    createdAt: string;
    children?: IMessage[];
}

export interface IMessagesState {
    sendingMessage: boolean;
    sentMessage: boolean;
    sendMessageErrs: SendMessageErrs;
    fetchingMessages: boolean;
    fetchMessagesErr: string | null;
    userMessages: IMessage[];
    rentalItemMessages: IMessage[];
    rentalItemAttachmentMessages: IMessage[];
    messages: IMessage[];
}

const initialState = {
    sendingMessage: false,
    sentMessage: false,
    sendMessageErrs: new SendMessageErrs(),
    fetchingMessages: false,
    fetchMessagesErr: null,
    userMessages: [],
    rentalItemAttachmentMessages: [],
    rentalItemMessages: [],
    messages: [],

} as IMessagesState;

const messagesSlice = createSlice({
    name: 'messagesSlice',
    initialState,
    reducers: {
        clearSendMessageFieldErr: (state: IMessagesState, action: PayloadAction<string>) => {
            state.sendMessageErrs[action.payload as keyof SendMessageErrs] = null;
        },
        clearSendMessageErrs: (state: IMessagesState) => {
            state.sendMessageErrs = new SendMessageErrs();
        },
        tryResetSendMessageFormErr: (state: IMessagesState) => {
            let shouldReset = true;

            for (let key in state.sendMessageErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.sendMessageErrs[key as keyof SendMessageErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.sendMessageErrs.form = null;
            }
        },
        resetSentMessage: (state: IMessagesState) => {
            state.sentMessage = false;
        },
        sendMessageStart: (state: IMessagesState) => {
            state.sendingMessage = true;
            state.sentMessage = false;
        },
        sendMessagePass: (state: IMessagesState) => {
            state.sentMessage = true;
            state.sendingMessage = false;
        },
        sendMessageFail: (state: IMessagesState, action: PayloadAction<IFieldError[]>) => {
            state.sendingMessage = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.sendMessageErrs) {
                    if (err.field === key) {
                        state.sendMessageErrs[key as keyof SendMessageErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.sendMessageErrs.form) {
                state.sendMessageErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchMessages: (state: IMessagesState) => {
            state.fetchMessagesErr = null;
        },

        clearFetchMessageErrs: (state: IMessagesState) => {
            state.fetchMessagesErr = null;

        },

        fetchMessagesStart: (state: IMessagesState) => {
            state.fetchingMessages = true;
        },
        fetchMessagesPass: (state: IMessagesState, action: PayloadAction<{ messages: IMessage[], targetType: string }>) => {

            const { messages, targetType } = action.payload;

            switch (targetType) {
                case USER_TARGET:
                    state.userMessages = messages;
                    break;
                case RENTAL_ITEM_TARGET:
                    state.rentalItemMessages = messages;
                    break;
                case RENTAL_ITEM_ATTACHMENT_TARGET:
                    state.rentalItemAttachmentMessages = messages;
                    break;
                default:
                    break;
            }

            state.fetchingMessages = false;

        },
        fetchMessagesFail: (state: IMessagesState, action: PayloadAction<string>) => {
            state.fetchingMessages = false;
            state.fetchMessagesErr = action.payload;
        },
    }
});

export const {
    clearSendMessageErrs,
    clearSendMessageFieldErr,
    tryResetSendMessageFormErr,
    resetSentMessage,
    sendMessageStart,
    sendMessagePass,
    sendMessageFail,
    clearFetchMessages,
    fetchMessagesStart,
    fetchMessagesPass,
    fetchMessagesFail,
} = messagesSlice.actions;

interface ITrySendMessageArg {
    authToken: string;
    data: {
        parentId?: string;
        senderUserId: string;
        targetType: string;
        targetId: string;
        content: string;
    }
}
export const trySendMessage = createAsyncThunk('messages/trySendMessage', (arg: ITrySendMessageArg, { dispatch }) => {

    const { authToken, data } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/message`;

    dispatch(sendMessageStart());
    axios.post(endpoint, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        //TODO: dispatch message-notification (socket.io) to the receive
        socket.emit(USER_MESSAGE_AVAILABLE, res.data.receiverUserId);

        dispatch(sendMessagePass());
    }).catch(err => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(sendMessageFail(fieldErrors));
    })

});

interface ITryFetchMessageArg {
    authToken: string;
    targetType: string;
    targetId: string;
}

export const tryFetchMessages = createAsyncThunk('messages/tryFetchMessages', (arg: ITryFetchMessageArg, { dispatch }) => {

    const { authToken, targetType, targetId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/messages?targetType=${targetType}&targetId=${targetId}`;

    dispatch(fetchMessagesStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        // alert(JSON.stringify(res.data));
        dispatch(fetchMessagesPass({ messages: res.data, targetType }));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(fetchMessagesFail(data.err));
        } else {
            dispatch(fetchMessagesFail('Check your network connection.'));
        }

    });
});

export default messagesSlice.reducer;